import { modelMatrix, type Placement } from "./props";
import type { GuildAttributeTable } from "../sim/collision";

/** One model's walkable surface out of `client/public/surfaces/surfaces.json`:
 *  the `.mdatr` height lists as flat triangles in the model's own frame, in
 *  millimetres, and the radius `CAttributeData::OnLoad` takes over them. */
export interface SurfaceModel {
  readonly radiusMm: number;
  readonly triangles: readonly number[];
}

export type SurfaceModels = Readonly<Record<string, SurfaceModel>>;

/** One standing guild building for `ObjectSurface.fromBuildings`: its vnum,
 *  where it stands, the ground under it and its facing in 4096 steps. */
export interface StandingSurface {
  readonly vnum: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly heading: number;
}

/** `tools/build_object_surfaces.py`'s output, at the path it writes. */
export const SURFACES_URL = "surfaces/surfaces.json";

/** How wide one bucket of the placement index is. Only a lookup cost: every
 *  instance is filed under each bucket its radius reaches. */
const BUCKET_MM = 16_000;

interface SurfaceInstance {
  readonly xMm: number;
  readonly yMm: number;
  readonly radiusMm: number;
  /** World-space triangles, nine numbers each. */
  readonly triangles: Float64Array;
}

/** `CrossProduct2D` (`EterLib/GrpMath.cpp:4-7`). */
function cross2D(x1: number, y1: number, x2: number, y2: number): number {
  return x1 * y2 - y1 * x2;
}

/** `IsInTriangle2D` (`EterLib/GrpMath.cpp:9-45`): strictly inside by the three
 *  edge cross products agreeing in sign, or on an edge's line and inside the
 *  triangle's box. Either winding. */
export function isInTriangle2D(
  ax: number, ay: number, bx: number, by: number, cx: number, cy: number, tx: number, ty: number,
): boolean {
  const c1 = cross2D(bx - ax, by - ay, tx - ax, ty - ay);
  const c2 = cross2D(cx - bx, cy - by, tx - bx, ty - by);
  const c3 = cross2D(ax - cx, ay - cy, tx - cx, ty - cy);
  if (c1 * c2 > 0 && c1 * c3 > 0) return true;
  if (c1 * c2 * c3 === 0) {
    if (tx < ax && tx < bx && tx < cx) return false;
    if (tx > ax && tx > bx && tx > cx) return false;
    if (ty < ay && ty < by && ty < cy) return false;
    if (ty > ay && ty > by && ty > cy) return false;
    return true;
  }
  return false;
}

/** Every placed building's and dungeon block's own walkable surface: the
 *  attribute instances `CArea` hangs on them (`Area.cpp:432-437,452-458`),
 *  transformed by the placement the way `CAttributeInstance::RefreshObject`
 *  transforms them by the thing's world matrix (`AttributeInstance.cpp:138-164`),
 *  and asked for a height the way `CMapOutdoor::GetHeight` asks them. */
export class ObjectSurface {
  private readonly buckets = new Map<number, SurfaceInstance[]>();

  constructor(models: SurfaceModels, placements: readonly Placement[]) {
    for (const placement of placements) {
      const model = models[placement.model];
      if (model === undefined || model.triangles.length === 0) continue;
      const m = modelMatrix(placement.position, placement.rotationDeg, placement.yawDeg, placement.pitchDeg);
      const local = model.triangles;
      const world = new Float64Array(local.length);
      for (let at = 0; at < local.length; at += 3) {
        const x = local[at];
        const y = local[at + 1];
        const z = local[at + 2];
        world[at] = m[0] * x + m[4] * y + m[8] * z + m[12];
        world[at + 1] = m[1] * x + m[5] * y + m[9] * z + m[13];
        world[at + 2] = m[2] * x + m[6] * y + m[10] * z + m[14];
      }
      const instance: SurfaceInstance = {
        xMm: placement.position.xMm,
        yMm: placement.position.yMm,
        radiusMm: model.radiusMm,
        triangles: world,
      };
      this.add(instance);
    }
  }

  /** Each standing guild building's height triangles at its transform, as
   *  `UpdateAttribute` hands its attribute instance to `SetHeightInstance`
   *  (`ActorInstanceData.cpp:20-37`, `ActorInstance.cpp:516-533`): model x and y
   *  turn by the heading and y flips into the world's, the placement
   *  `guildBuildingCollision` gives the same records; z stands on `zMm`. */
  static fromBuildings(table: GuildAttributeTable, buildings: readonly StandingSurface[]): ObjectSurface {
    const surface = new ObjectSurface({}, []);
    for (const building of buildings) {
      const row = table.buildings[String(building.vnum)];
      if (row === undefined || row.surface.triangles.length === 0) continue;
      const angle = (building.heading * 2 * Math.PI) / 4096;
      const c = Math.cos(angle);
      const s = Math.sin(angle);
      const local = row.surface.triangles;
      const world = new Float64Array(local.length);
      for (let at = 0; at + 2 < local.length; at += 3) {
        world[at] = building.xMm + (local[at] * c - local[at + 1] * s);
        world[at + 1] = building.yMm - (local[at] * s + local[at + 1] * c);
        world[at + 2] = building.zMm + local[at + 2];
      }
      surface.add({ xMm: building.xMm, yMm: building.yMm, radiusMm: row.surface.radiusMm, triangles: world });
    }
    return surface;
  }

  private add(instance: SurfaceInstance): void {
    const x0 = Math.floor((instance.xMm - instance.radiusMm) / BUCKET_MM);
    const x1 = Math.floor((instance.xMm + instance.radiusMm) / BUCKET_MM);
    const y0 = Math.floor((instance.yMm - instance.radiusMm) / BUCKET_MM);
    const y1 = Math.floor((instance.yMm + instance.radiusMm) / BUCKET_MM);
    for (let by = y0; by <= y1; by += 1) {
      for (let bx = x0; bx <= x1; bx += 1) {
        const key = bucketKey(bx, by);
        const list = this.buckets.get(key);
        if (list === undefined) this.buckets.set(key, [instance]);
        else list.push(instance);
      }
    }
  }

  /** The highest object surface over a point, or null where none is.
   *
   *  `FGetObjectHeight` hands one answer, starting at 0.0f, to every instance
   *  the point falls under (`MapOutdoor.cpp:19-37`); an instance further from
   *  the point than its radius says nothing (`AttributeInstance.cpp:73-74,
   *  122-130`), and each triangle whose box and outline hold the point raises
   *  the answer to its plane's height there (`:78-112`). */
  heightAt(xMm: number, yMm: number): number | null {
    const list = this.buckets.get(bucketKey(Math.floor(xMm / BUCKET_MM), Math.floor(yMm / BUCKET_MM)));
    if (list === undefined) return null;
    let height = 0;
    let found = false;
    for (const instance of list) {
      if (Math.hypot(instance.xMm - xMm, instance.yMm - yMm) > instance.radiusMm) continue;
      const t = instance.triangles;
      for (let at = 0; at + 8 < t.length; at += 9) {
        const x0 = t[at], y0 = t[at + 1], z0 = t[at + 2];
        const x1 = t[at + 3], y1 = t[at + 4], z1 = t[at + 5];
        const x2 = t[at + 6], y2 = t[at + 7], z2 = t[at + 8];
        if (
          (xMm < x0 && xMm < x1 && xMm < x2) ||
          (xMm > x0 && xMm > x1 && xMm > x2) ||
          (yMm < y0 && yMm < y1 && yMm < y2) ||
          (yMm > y0 && yMm > y1 && yMm > y2)
        ) continue;
        if (!isInTriangle2D(x0, y0, x1, y1, x2, y2, xMm, yMm)) continue;
        const ux = x1 - x0, uy = y1 - y0, uz = z1 - z0;
        const vx = x2 - x0, vy = y2 - y0, vz = z2 - z0;
        let nx = uy * vz - uz * vy;
        let ny = uz * vx - ux * vz;
        let nz = ux * vy - uy * vx;
        const length = Math.hypot(nx, ny, nz);
        if (length === 0) continue;
        nx /= length;
        ny /= length;
        nz /= length;
        if (nz === 0) continue;
        const d = nx * x0 + ny * y0 + nz * z0;
        height = Math.max((d - (nx * xMm + ny * yMm)) / nz, height);
        found = true;
      }
    }
    return found ? height : null;
  }
}

function bucketKey(bx: number, by: number): number {
  return by * 1_000_003 + bx;
}

/** The surfaces manifest, or an empty one when it cannot be fetched: a map
 *  whose objects cannot be stood on still has its terrain. */
export async function loadSurfaceModels(fetchFn: typeof fetch = fetch): Promise<SurfaceModels> {
  try {
    const response = await fetchFn(SURFACES_URL);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { models?: SurfaceModels };
    return body.models ?? {};
  } catch (error) {
    console.warn(`${SURFACES_URL} unavailable (${String(error)}); objects are not ground`);
    return {};
  }
}
